import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';

import { PrimaryButton } from 'brand-guidelines/Buttons-styled-components';
import { COUNTRY } from 'common/constants';
import { FieldValidation, useFormValidator } from 'common/Validator/Validator';
import { UpdatePatientDetails } from 'connect/saga/handlers/patient/update-details';
import { selectDataClinicCountry, selectDataHasInsurance, selectDataInsurance, selectInsuranceListForCountry } from 'connect/selectors';
import { InputType } from 'pages/checkout/components/common/Input';
import { FormError } from 'pages/checkout/components/common/input-styled-components';
import { StepFormWrapper, StepHeader, StepHeaderSubtext, StepHeaderText } from 'pages/checkout/components/common/layout-styled-components';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutInsurancePlan, CheckoutInsuranceProvider, CheckoutState } from 'pages/checkout/connect/types';
import { DAY_OPTIONS, MONTH_OPTIONS, YEAR_OPTIONS } from 'pages/checkout/constants';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { useCheckoutRedesignAnalyticsData } from 'pages/checkout/utils/analyticsUtils';
import { formatBirthDate, updateDayOptions } from 'pages/checkout/utils/timeUtils';
import { BooleanChoice, booleanChoiceToBoolean, booleanToBooleanChoice } from 'types/BooleanChoice';
import { usePatch } from 'utils/customHooks/usePatch';

import { InsurancePicker } from './components/InsurancePicker';
import { MemberIdHelper } from './components/MemberIdHelper';
import { INSURANCE_FORM_VALIDATOR_CONFIG } from './formValidatorConfigs';
import { FormRow, InsuranceStepWrapper, SectionHeader, SectionHeaderWrapper, SectionSubheader } from './Insurance-styled-components';
import { commonBirthDateConfig, FormInputs } from './rendererHelpers';

const booleanChoices = [BooleanChoice.Yes, BooleanChoice.No];

const analytics = new Analytics();

const getInsurancePlanFromProvider = (insuranceProvider: any, insurancePlanName: string) => {
  if (!insuranceProvider?.plans?.length) {
    return null;
  }

  return insuranceProvider.plans?.find((plan: any) => plan.name === insurancePlanName);
};

interface ValidationState {
  allValid?: boolean;
  hasInsurance?: FieldValidation;
  insuranceProvider?: FieldValidation;
  insurancePlan?: FieldValidation;
  memberId?: FieldValidation;
  groupId?: FieldValidation;
  isPrimaryMember?: FieldValidation;
  primaryMemberFirstName?: FieldValidation;
  primaryMemberLastName?: FieldValidation;
  primaryMemberBirthYear?: FieldValidation;
  primaryMemberBirthMonth?: FieldValidation;
  primaryMemberBirthDay?: FieldValidation;
}

export const InsuranceInfo = () => {
  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-insurance-information',
      name: 'checkout',
      section: 'insurance-information',
      ...analyticsData,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- analyticsData is a new object every time, so we don't want it in the dependency array
  }, []);

  const insuranceList = useSelector(selectInsuranceListForCountry);
  const { insurance: insuranceState } = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const analyticsData = useCheckoutRedesignAnalyticsData();

  const isLoading = useSelector(UpdatePatientDetails.loading!.selector);
  const error = useSelector(UpdatePatientDetails.error!.selector);
  const success = useSelector(UpdatePatientDetails.success!.selector);

  const clinicCountry = useSelector(selectDataClinicCountry);
  const isUsClinic = clinicCountry === COUNTRY.unitedStates;

  const insuranceFormValidator = useFormValidator(INSURANCE_FORM_VALIDATOR_CONFIG);

  const {
    provider: storedInsuranceProvider,
    plan: storedInsurancePlan,
    memberId: storedMemberId,
    groupId: storedGroupId,
    isPrimaryMember: storedIsPrimaryMember,
    primaryMember: storedPrimaryMember,
  } = insuranceState || {};

  const {
    firstName: storedPrimaryMemberFirstName,
    lastName: storedPrimaryMemberLastName,
    dateOfBirth: storedPrimaryMemberDateOfBirth = {
      year: null,
      month: null,
      day: null,
    },
  } = storedPrimaryMember || {};

  const matchHasInsurance = useSelector(selectDataHasInsurance);
  // Only show has insurance question if no insurance data is in the match
  // match.other.hasInsurance defaults to null, and is a string if filled
  const showHasInsuranceQuestion = matchHasInsurance === null;

  const matchInsuranceData = useSelector(selectDataInsurance);
  const {
    provider: matchInsuranceProviderName,
    plan: matchInsurancePlanName,
  }: { provider: string; plan: string; } = matchInsuranceData || {};

  const getInsuranceProviderFromList = useCallback((insuranceProviderName: string) =>
    insuranceList?.find((insurance: any) => insurance.name === insuranceProviderName), [insuranceList]);

  /* Handle prefilling insurance details */
  useEffect(() => {
    // Prioritize provider and plan from Redux over match data
    const hasStoredInsuranceProvider = Boolean(storedInsuranceProvider?.name && storedInsuranceProvider?.id);
    const insuranceProviderToPrefill = hasStoredInsuranceProvider
      ? storedInsuranceProvider
      : matchInsuranceProviderName;

    const hasStoredInsurancePlan = Boolean(storedInsurancePlan?.name && storedInsurancePlan?.id);
    const insurancePlanToPrefill = hasStoredInsurancePlan
      ? storedInsurancePlan
      : matchInsurancePlanName;

    if (insuranceProviderToPrefill || insurancePlanToPrefill) {
      const fullInsuranceProvider = (() => {
        if (Object.is(insuranceProviderToPrefill, matchInsuranceProviderName)) {
          return getInsuranceProviderFromList(insuranceProviderToPrefill as unknown as string);
        }

        return insuranceProviderToPrefill;
      })();

      if (!fullInsuranceProvider) {
        return;
      }

      setInsuranceProvider({
        id: fullInsuranceProvider.id,
        name: fullInsuranceProvider.name,
      } as CheckoutInsuranceProvider);

      const fullInsurancePlan = (() => {
        if (Object.is(insurancePlanToPrefill, matchInsurancePlanName)) {
          return getInsurancePlanFromProvider(fullInsuranceProvider, insurancePlanToPrefill as unknown as string);
        }

        return insurancePlanToPrefill;
      })();

      if (!fullInsurancePlan) {
        return;
      }

      setInsurancePlan({
        id: fullInsurancePlan.id,
        name: fullInsurancePlan.name,
      } as CheckoutInsurancePlan);
    }
  }, [
    getInsuranceProviderFromList,
    matchInsurancePlanName,
    matchInsuranceProviderName,
    storedInsurancePlan,
    storedInsuranceProvider,
  ]);

  const [hasInsurance, setHasInsurance] = useState<BooleanChoice | null>(matchHasInsurance);
  const [
    insuranceProvider,
    setInsuranceProvider,
  ] = useState<CheckoutInsuranceProvider | null>(null);
  const [
    insurancePlan,
    setInsurancePlan,
  ] = useState<CheckoutInsurancePlan | null>(null);
  const [
    memberId,
    setMemberId,
  ] = useState<string>(storedMemberId || '');
  const [
    groupId,
    setGroupId,
  ] = useState<string>(storedGroupId || '');

  const [
    isPrimaryMember,
    setIsPrimaryMember,
  ] = useState<BooleanChoice | null>(booleanToBooleanChoice(storedIsPrimaryMember) || null);
  const [primaryMemberFirstName, setPrimaryMemberFirstName] = useState<string>(storedPrimaryMemberFirstName || '');
  const [primaryMemberLastName, setPrimaryMemberLastName] = useState<string>(storedPrimaryMemberLastName || '');
  const [
    primaryMemberBirthYear,
    setPrimaryMemberBirthYear,
  ] = useState<string | null>(storedPrimaryMemberDateOfBirth.year || null);
  const [
    primaryMemberBirthMonth,
    setPrimaryMemberBirthMonth,
  ] = useState<string | null>(storedPrimaryMemberDateOfBirth.month || null);
  const [
    primaryMemberBirthDay,
    setPrimaryMemberBirthDay,
  ] = useState<string | null>(storedPrimaryMemberDateOfBirth.day || null);

  const [dayOptions, setDayOptions] = useState(DAY_OPTIONS);
  const [formValidity, setFormValidity] = useState<ValidationState>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const formState = useMemo(() => ({
    hasInsurance,
    insuranceProvider,
    insurancePlan,
    memberId,
    groupId,
    isPrimaryMember,
    primaryMemberFirstName,
    primaryMemberLastName,
    primaryMemberBirthYear,
    primaryMemberBirthMonth,
    primaryMemberBirthDay,
  }), [
    hasInsurance,
    insuranceProvider,
    insurancePlan,
    memberId,
    groupId,
    isPrimaryMember,
    primaryMemberFirstName,
    primaryMemberLastName,
    primaryMemberBirthYear,
    primaryMemberBirthMonth,
    primaryMemberBirthDay,
  ]);

  const handleValidate = useCallback(() => {
    const hasSelectedNoInsurance = hasInsurance === BooleanChoice.No;
    const isPrimaryMemberBool = booleanChoiceToBoolean(isPrimaryMember);
    const validationResult = insuranceFormValidator.validate(
      formState,
      { isUsClinic, isPrimaryMember: isPrimaryMemberBool, showHasInsuranceQuestion, hasSelectedNoInsurance },
    );

    setFormValidity(validationResult);
    return validationResult;
  }, [formState, hasInsurance, showHasInsuranceQuestion, isPrimaryMember, insuranceFormValidator, isUsClinic]);

  // Validate on form state change
  useEffect(() => {
    if (!formState) {
      return;
    }

    handleValidate();
  }, [handleValidate, formState]);

  const updateValidDays = ({ selectedYear, selectedMonth }: { selectedYear?: string; selectedMonth?: string; }) => {
    const filteredDayOptions = updateDayOptions({
      selectedYear,
      selectedMonth,
      savedYear: primaryMemberBirthYear,
      savedMonth: primaryMemberBirthMonth,
    });

    // Update day options if needed
    setDayOptions((prevDayOptions) => {
      if (prevDayOptions.length === filteredDayOptions.length) {
        return prevDayOptions;
      }
      return filteredDayOptions;
    });

    if (!filteredDayOptions.some((option) => option.id === primaryMemberBirthDay)) {
      setPrimaryMemberBirthDay(null);
    }
  };

  const patchCheckout = usePatch<CheckoutState>({
    stateHandler: CheckoutStateHandler,
  });

  const handleHasInsuranceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newHasInsurance = event.target.value as BooleanChoice;
    setHasInsurance(newHasInsurance);
  };

  const handleMemberIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMemberId = event.target.value;
    setMemberId(newMemberId);
  };

  const handleGroupIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newGroupId = event.target.value;
    setGroupId(newGroupId);
  };

  const handleIsPrimaryMemberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newIsPrimaryMember = event.target.value as BooleanChoice;
    setIsPrimaryMember(newIsPrimaryMember);

    if (newIsPrimaryMember === BooleanChoice.No) {
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    }
  };

  const handlePrimaryMemberFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFirstName = event.target.value;
    setPrimaryMemberFirstName(newFirstName);
  };

  const handlePrimaryMemberLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLastName = event.target.value;
    setPrimaryMemberLastName(newLastName);
  };

  const handleYearSelect = (selectedYear: string) => {
    setPrimaryMemberBirthYear(selectedYear);

    if (selectedYear) {
      updateValidDays({ selectedYear });
    }
  };

  const handleMonthSelect = (selectedMonth: string) => {
    setPrimaryMemberBirthMonth(selectedMonth);

    if (selectedMonth) {
      updateValidDays({ selectedMonth });
    }
  };

  const handleDaySelect = (selectedDay: string) => {
    setPrimaryMemberBirthDay(selectedDay);
  };

  const dispatch = useDispatch();
  const { goToNextStep } = useNextCheckoutStep();

  const handleInsuranceProviderSelect = (provider: CheckoutInsuranceProvider | null) => {
    if (!provider) {
      setInsuranceProvider(null);
      return;
    }

    const { id, name } = provider;
    setInsuranceProvider({ id, name });
  };

  const handleInsurancePlanSelect = (plan: CheckoutInsurancePlan | null) => {
    if (!plan) {
      setInsurancePlan(null);
      return;
    }

    const { id, name } = plan;
    setInsurancePlan({ id, name });
  };

  // If form is invalid, show validation errors when clicking the button
  const handleDisabledButtonClick = () => {
    if (formValidity.allValid) {
      return;
    }

    setShowValidationErrors(true);
  };

  const handleContinue = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationResult = handleValidate();

    if (!validationResult.allValid) {
      setShowValidationErrors(true);
      return;
    }

    const primaryMemberData = isPrimaryMember === BooleanChoice.No
      ? {
        primaryMember: {
          firstName: primaryMemberFirstName,
          lastName: primaryMemberLastName,
          dateOfBirth: {
            year: primaryMemberBirthYear!,
            month: primaryMemberBirthMonth!,
            day: primaryMemberBirthDay!,
          },
        },
      }
      : undefined;

    const insuranceDetails = {
      hasInsurance: matchHasInsurance || booleanChoiceToBoolean(hasInsurance),
      provider: insuranceProvider,
      plan: insurancePlan,
      memberId,
      groupId,
      isPrimaryMember: booleanChoiceToBoolean(isPrimaryMember),
      ...primaryMemberData,
    };

    const birthDateString = isPrimaryMember === BooleanChoice.No
      ? formatBirthDate({ year: primaryMemberBirthYear!, month: primaryMemberBirthMonth!, day: primaryMemberBirthDay! })
      : undefined;

    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-insurance-information',
      name: 'checkout',
      section: 'insurance-information',
      content: {
        provider: String(insuranceProvider?.name),
        plan: String(insurancePlan?.name),
        is_primary_member: String(isPrimaryMember),
        birth_date: birthDateString,
        ...analyticsData.content,
      },
    });

    patchCheckout({ insurance: insuranceDetails });

    const patientMetadata: any = {};

    patientMetadata.primaryInsuranceMember = insuranceDetails.isPrimaryMember;

    if (!insuranceDetails.isPrimaryMember) {
      const memberDateOfBirth = birthDateString;

      Object.assign(patientMetadata, {
        memberFirstName: primaryMemberFirstName,
        memberLastName: primaryMemberLastName,
        memberDateOfBirth,
      });
    }

    dispatch(UpdatePatientDetails.createAction({ metadata: patientMetadata }));
  };

  // Watch for success state changes and navigate to next step
  useEffect(() => {
    if (!success || isLoading) {
      return;
    }

    dispatch(UpdatePatientDetails.success!.update(null));
    goToNextStep();
  }, [success, isLoading, dispatch, goToNextStep]);

  const hasInsuranceInputs = [
    {
      id: 'has-insurance',
      label: 'Do you have insurance?',
      type: InputType.TOGGLE,
      required: showHasInsuranceQuestion,
      options: booleanChoices,
      value: hasInsurance as string,
      onChange: handleHasInsuranceChange,
      errorMessage: showValidationErrors ? formValidity.hasInsurance?.message : undefined,
    },
  ];

  // Note - in future we can abstract these further to be a reusable form system
  const insuranceInputs = [
    {
      id: 'memberIdInput',
      label: 'Member ID',
      tooltip: 'This is the ID number assigned to you by your insurance carrier.',
      type: InputType.TEXT,
      required: true,
      value: memberId,
      onChange: handleMemberIdChange,
      errorMessage: showValidationErrors ? formValidity.memberId?.message : undefined,
      endContent: <MemberIdHelper insuranceProviderName={insuranceProvider?.name} />,
    },
    ...!isUsClinic
      ? []
      : [{
        label: 'Group ID',
        subtext: '(optional)',
        type: InputType.TEXT,
        value: groupId,
        onChange: handleGroupIdChange,
      }],
    {
      label: 'Are you the primary member/subscriber on your dental insurance plan?',
      type: InputType.TOGGLE,
      required: true,
      isSensitive: false,
      options: booleanChoices,
      value: isPrimaryMember as string,
      onChange: handleIsPrimaryMemberChange,
      errorMessage: showValidationErrors ? formValidity.isPrimaryMember?.message : undefined,
    },
  ];

  const primaryMemberNameInputs = [
    {
      label: 'First Name',
      autoComplete: 'given-name',
      type: InputType.TEXT,
      required: true,
      value: primaryMemberFirstName,
      onChange: handlePrimaryMemberFirstNameChange,
      errorMessage: showValidationErrors ? formValidity.primaryMemberFirstName?.message : undefined,
    },
    {
      label: 'Last Name',
      autoComplete: 'family-name',
      type: InputType.TEXT,
      required: true,
      value: primaryMemberLastName,
      onChange: handlePrimaryMemberLastNameChange,
      errorMessage: showValidationErrors ? formValidity.primaryMemberLastName?.message : undefined,
    },
  ];

  const primaryMemberDateOfBirthInputs = [
    {
      ...commonBirthDateConfig,
      label: 'Birth Month',
      required: true,
      options: MONTH_OPTIONS,
      value: primaryMemberBirthMonth,
      onSelect: handleMonthSelect,
      errorMessage: showValidationErrors ? formValidity.primaryMemberBirthMonth?.message : undefined,
    },
    {
      ...commonBirthDateConfig,
      label: 'Birth Day',
      required: true,
      options: dayOptions,
      value: primaryMemberBirthDay,
      onSelect: handleDaySelect,
      errorMessage: showValidationErrors ? formValidity.primaryMemberBirthDay?.message : undefined,
    },
    {
      ...commonBirthDateConfig,
      label: 'Birth Year',
      required: true,
      options: YEAR_OPTIONS,
      value: primaryMemberBirthYear,
      onSelect: handleYearSelect,
      errorMessage: showValidationErrors ? formValidity.primaryMemberBirthYear?.message : undefined,
    },
  ];

  return (
    <InsuranceStepWrapper onSubmit={handleContinue}>
      <StepHeader>
        <StepHeaderText>Insurance Information</StepHeaderText>
        <StepHeaderSubtext>
          This information will be sent to the practice so they can check your coverage.
        </StepHeaderSubtext>
      </StepHeader>

      {showHasInsuranceQuestion
      && (
        <StepFormWrapper>
          <FormInputs inputConfigs={hasInsuranceInputs} />
        </StepFormWrapper>
      )}

      {(!showHasInsuranceQuestion || hasInsurance === BooleanChoice.Yes)
      && (
        <>
          <StepFormWrapper>
            {error && <FormError>{error}</FormError>}
            <InsurancePicker
              selectedInsuranceProvider={insuranceProvider}
              onSelectInsuranceProvider={handleInsuranceProviderSelect}
              selectedInsurancePlan={insurancePlan}
              onSelectInsurancePlan={handleInsurancePlanSelect}
              providerErrorMessage={showValidationErrors ? formValidity.insuranceProvider?.message : undefined}
              planErrorMessage={showValidationErrors ? formValidity.insurancePlan?.message : undefined}
            />
            <FormInputs inputConfigs={insuranceInputs} />
          </StepFormWrapper>

          {isPrimaryMember === BooleanChoice.No
          && (
            <StepFormWrapper>
              <SectionHeaderWrapper>
                <SectionHeader>Member Information</SectionHeader>
                <SectionSubheader>Who is the primary member/subscriber?</SectionSubheader>
              </SectionHeaderWrapper>
              <FormRow>
                <FormInputs inputConfigs={primaryMemberNameInputs} />
              </FormRow>

              <FormRow>
                <FormInputs inputConfigs={primaryMemberDateOfBirthInputs} />
              </FormRow>
            </StepFormWrapper>
          )}
        </>
      )}
      <PrimaryButton
        type="submit"
        onClick={handleDisabledButtonClick}
        $loading={isLoading}
        $disabled={!formValidity.allValid}
      >
        Continue
      </PrimaryButton>
    </InsuranceStepWrapper>
  );
};
